import { useState } from 'react';
import { TacticStatus } from 'basics/enums/maya.enums';
import useGetAllTacticsLazy from 'basics/graphql/queries/getAllTacticsLazy';
import { useFetchTacticApprovedDocumentsOptions } from 'components/TacticForm/hooks/approvedDocuments.hook';
import { useFetchTacticIVADocumentsOptions } from 'components/TacticForm/hooks/IVADocuments.hook';
import { useFetchTacticDropMaterialsOptions } from 'components/TacticForm/hooks/TacticDropMaterials.hook';
import { useFetchTacticKeyMessagesOptions } from 'components/TacticForm/hooks/TacticKeyMessages.hook';
import {
  Channel,
  CustomerInitiative,
  Tactic,
} from 'generated/maya.types';

export type InvalidTacticField = {
  tactic: Tactic;
  validationIssues: string[];
};

export type CepDateValidationError = {
  cepDateStart: string;
  cepDateEnd: string;
  ciDateStart: string;
  ciDateEnd: string;
};

type ValidateTacticsFieldsOptions = {
  validateRequiredFields?: boolean;
  validateOptionsAvailability?: boolean;
};

const KEY_MESSAGE_REQUIRED_CHANNELS: Channel[] = [
  Channel.f2fMeeting,
  Channel.oneToOne,
  Channel.oneToOneKeyMessage,
  Channel.telephone,
  Channel.virtualMeeting,
];

const KEY_MESSAGE_CHANNELS: Channel[] = [
  Channel.f2fMeeting,
  Channel.oneToOne,
  Channel.oneToOneKeyMessage,
  Channel.telephone,
  Channel.virtualMeeting,
];

const DROP_MATERIAL_CHANNELS: Channel[] = [
  Channel.f2fEvent,
  Channel.f2fMeeting,
  Channel.hybridEvent,
  Channel.oneToOne,
  Channel.oneToOneDropMaterial,
];

const IVA_DOCUMENT_CHANNELS: Channel[] = [
  Channel.f2fEvent,
  Channel.f2fMeeting,
  Channel.oneToOne,
  Channel.oneToOneIVA,
  Channel.hybridEvent,
  Channel.virtualEvent,
  Channel.virtualMeeting,
];

const APPROVED_DOCUMENT_CHANNELS: Channel[] = [
  Channel.approvedEmail,
];

const hasSameId = (firstValue?: string | number | null, secondValue?: string | number | null): boolean => {
  if (!firstValue || !secondValue) {
    return false;
  }

  return firstValue.toString() === secondValue.toString();
};

const getTacticCountry = (tactic: Tactic): string | undefined => {
  const customerInitiative = tactic.cep?.customerInitiative as CustomerInitiative | undefined;

  return customerInitiative?.country;
};

const getTacticProduct = (tactic: Tactic): string | undefined => {
  return tactic.cep?.product || undefined;
};

const isTacticDateOutOfCEPRange = (tactic: Tactic): boolean => {
  const cepDateStart = tactic.cep?.dateStart;
  const cepDateEnd = tactic.cep?.dateEnd;

  if (!cepDateStart || !cepDateEnd) {
    return false;
  }

  const cepStart = new Date(cepDateStart).getTime();
  const cepEnd = new Date(cepDateEnd).getTime();

  if (tactic.dateStart) {
    const tacticStart = new Date(tactic.dateStart).getTime();
    if (tacticStart < cepStart || tacticStart > cepEnd) {
      return true;
    }
  }

  if (tactic.dateEnd) {
    const tacticEnd = new Date(tactic.dateEnd).getTime();
    if (tacticEnd < cepStart || tacticEnd > cepEnd) {
      return true;
    }
  }

  return false;
};

const validateTacticFields = (tactic: Tactic): string[] => {
  const validationIssues: string[] = [];
  const { channel } = tactic;

  if (channel === Channel.oneToOneIVA && !tactic.IVADocumentId) {
    validationIssues.push('IVADocumentRequired');
  }

  if (channel === Channel.oneToOneDropMaterial && !tactic.dropMaterial?.id) {
    validationIssues.push('dropMaterialRequired');
  }

  if (KEY_MESSAGE_REQUIRED_CHANNELS.includes(channel) && !tactic.keyMessage?.id) {
    validationIssues.push('keyMessageRequired');
  }

  if (isTacticDateOutOfCEPRange(tactic)) {
    validationIssues.push('dateRangeRequired');
  }

  return validationIssues;
};

const isCepDateOutOfCIRange = (tactic: Tactic): CepDateValidationError | null => {
  const { cep } = tactic;
  if (!cep) {
    return null;
  }

  const ciDateStart = cep.customerInitiative?.dateStart;
  const ciDateEnd = cep.customerInitiative?.dateEnd;

  if (!ciDateStart || !ciDateEnd || !cep.dateStart || !cep.dateEnd) {
    return null;
  }

  const ciStart = new Date(ciDateStart).getTime();
  const ciEnd = new Date(ciDateEnd).getTime();
  const cepStart = new Date(cep.dateStart).getTime();
  const cepEnd = new Date(cep.dateEnd).getTime();

  if (cepStart < ciStart || cepStart > ciEnd || cepEnd < ciStart || cepEnd > ciEnd) {
    return {
      cepDateStart: cep.dateStart,
      cepDateEnd: cep.dateEnd,
      ciDateStart,
      ciDateEnd,
    };
  }

  return null;
};

const useTacticFieldsValidation = () => {
  const [invalidTactics, setInvalidTactics] = useState<InvalidTacticField[]>([]);
  const [cepDateError, setCepDateError] = useState<CepDateValidationError | null>(null);
  const [validationError, setValidationError] = useState(false);
  const { fetchTactics } = useGetAllTacticsLazy();
  const { fetchTacticKeyMessagesOptions } = useFetchTacticKeyMessagesOptions();
  const { fetchTacticDropMaterialsOptions } = useFetchTacticDropMaterialsOptions();
  const { fetchTacticIVADocumentsOptions } = useFetchTacticIVADocumentsOptions();
  const { fetchTacticApprovedDocumentsOptions } = useFetchTacticApprovedDocumentsOptions();

  const validateKeyMessageAvailability = async (tactic: Tactic): Promise<string[]> => {
    if (!KEY_MESSAGE_CHANNELS.includes(tactic.channel) || !tactic.keyMessage?.id) {
      return [];
    }

    const shouldAllowPersistedKeyMessage = Boolean(tactic.keyMessage?.id);

    const keyMessagesOptions = await fetchTacticKeyMessagesOptions({
      country: getTacticCountry(tactic),
      product: getTacticProduct(tactic),
      executingTeam: tactic.executingTeam || undefined,
      selectedKeyMessage: tactic.keyMessage,
      includeSelectedKeyMessage: shouldAllowPersistedKeyMessage,
    });

    const selectedKeyMessage = keyMessagesOptions.find((keyMessage) => {
      return keyMessage.id === String(tactic.keyMessage?.id);
    });

    return selectedKeyMessage ? [] : ['keyMessageUnavailable'];
  };

  const validateDropMaterialAvailability = async (tactic: Tactic): Promise<string[]> => {
    if (!DROP_MATERIAL_CHANNELS.includes(tactic.channel) || !tactic.dropMaterial?.id) {
      return [];
    }

    const dropMaterialsOptions = await fetchTacticDropMaterialsOptions({
      country: getTacticCountry(tactic),
      product: getTacticProduct(tactic),
      executingTeam: tactic.executingTeam || undefined,
    });

    const selectedDropMaterial = dropMaterialsOptions.find((dropMaterial) => {
      return hasSameId(dropMaterial.id, tactic.dropMaterial?.id);
    });

    return selectedDropMaterial ? [] : ['dropMaterialUnavailable'];
  };

  const validateIVADocumentAvailability = async (tactic: Tactic): Promise<string[]> => {
    if (!IVA_DOCUMENT_CHANNELS.includes(tactic.channel) || !tactic.IVADocumentId) {
      return [];
    }

    const IVADocumentsOptions = await fetchTacticIVADocumentsOptions({
      country: getTacticCountry(tactic),
      product: getTacticProduct(tactic),
      executingTeam: tactic.executingTeam || undefined,
      includeNotCertifiedDocuments: false,
    });

    const selectedDocument = IVADocumentsOptions.find((document) => {
      return hasSameId(document.id, tactic.IVADocumentId);
    });

    return selectedDocument ? [] : ['IVADocumentUnavailable'];
  };

  const validateApprovedDocumentAvailability = async (tactic: Tactic): Promise<string[]> => {
    if (!APPROVED_DOCUMENT_CHANNELS.includes(tactic.channel) || !tactic.approvedDocumentId) {
      return [];
    }

    const approvedDocumentsOptions = await fetchTacticApprovedDocumentsOptions({
      country: getTacticCountry(tactic),
      product: getTacticProduct(tactic),
      executingTeam: tactic.executingTeam || undefined,
      currentDocumentId: tactic.approvedDocumentId,
      includeNotCertifiedDocuments: false,
    });

    const selectedDocument = approvedDocumentsOptions.find((document) => {
      return hasSameId(document.id, tactic.approvedDocumentId);
    });

    return selectedDocument ? [] : ['approvedDocumentUnavailable'];
  };

  const validateTacticOptionsAvailability = async (tactic: Tactic): Promise<string[]> => {
    const [
      keyMessageIssues,
      dropMaterialIssues,
      IVADocumentIssues,
      approvedDocumentIssues,
    ] = await Promise.all([
      validateKeyMessageAvailability(tactic),
      validateDropMaterialAvailability(tactic),
      validateIVADocumentAvailability(tactic),
      validateApprovedDocumentAvailability(tactic),
    ]);

    return [
      ...keyMessageIssues,
      ...dropMaterialIssues,
      ...IVADocumentIssues,
      ...approvedDocumentIssues,
    ];
  };

  const validateTacticsFields = async (
    cepId: string,
    options: ValidateTacticsFieldsOptions = {},
  ): Promise<boolean> => {
    const {
      validateRequiredFields = true,
      validateOptionsAvailability = false,
    } = options;

    const { data, error } = await fetchTactics({
      cep: cepId,
      status: [TacticStatus.EMPTY, TacticStatus.DRAFT, TacticStatus.APPROVED],
    });

    if (error || !data) {
      setInvalidTactics([]);
      setCepDateError(null);
      setValidationError(true);

      return false;
    }

    setValidationError(false);

    const { tactics } = data;
    const nonGroupTactics = tactics.filter((tactic) => tactic.channel !== Channel.group);

    const cepDateValidationError = nonGroupTactics.length > 0
      ? isCepDateOutOfCIRange(nonGroupTactics[0])
      : null;
    setCepDateError(cepDateValidationError);

    const invalid: InvalidTacticField[] = [];

    await Promise.all(nonGroupTactics.map(async (tactic) => {
      const validationIssues = [
        ...validateRequiredFields
          ? validateTacticFields(tactic)
          : [],
        ...validateOptionsAvailability
          ? await validateTacticOptionsAvailability(tactic)
          : [],
      ];

      if (validationIssues.length > 0) {
        invalid.push({
          tactic,
          validationIssues: Array.from(new Set(validationIssues)),
        });
      }
    }));

    setInvalidTactics(invalid);
    return invalid.length === 0 && !cepDateValidationError;
  };

  return {
    invalidTactics,
    cepDateError,
    validationError,
    validateTacticsFields,
  };
};

export default useTacticFieldsValidation;
